import { Pagination, EventRule } from '@ekkogmbh/apisdk';
import Permissions from 'src/Common/Permissions';
import { useCallback, useState } from 'react';
import { ColumnDef, DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { Add, Delete, Edit, Engineering } from '@mui/icons-material';
import { EventRulePanel } from './EventRulePanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useContentControl } from 'src/Common/Providers/ContentControlProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { useNavigate } from 'react-router';
import usePagination from 'src/Common/Providers/PaginationProvider';
import ScrollableStack from 'src/Common/Components/ScrollableStack';

// Shared minimum row height keeps scroll indicators vertically aligned across
// all proportional columns in the table.
const ROW_MIN_HEIGHT = 48;

const columns: ColumnDef[] = [
  {
    header: 'Name',
    body: (entry: EventRule, searchText?: string) => (
      <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
        {searchText ? dataTableHighlight(entry.name, [searchText]) : entry.name}
      </ScrollableStack>
    ),
    filterField: 'name',
    sortField: 'ER.name',
    proportionalContent: '25%',
  },
  {
    header: 'Coordinate',
    body: (entry: EventRule, searchText?: string) => (
      <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
        {searchText ? dataTableHighlight(entry.coordinate, [searchText]) : entry.coordinate}
      </ScrollableStack>
    ),
    filterField: 'coordinate',
    sortField: 'N.materializedPath',
    proportionalContent: '45%',
  },
  {
    header: 'Trigger',
    // Short enum string — fitContent shrinks to content; DataTable auto-highlights plain strings.
    body: (entry: EventRule) => entry.trigger.type,
    fitContent: true,
  },
  {
    header: 'Action',
    // Short enum string — fitContent shrinks to content; DataTable auto-highlights plain strings.
    body: (entry: EventRule) => entry.action.type,
    fitContent: true,
  },
];

export const EventRuleManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { setPanelOpen } = useContentControl();
  const { refresh } = usePagination();
  const navigate = useNavigate();

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedEventRule, setSelectedEventRule] = useState<EventRule>();

  const [, startLoading] = useRequest();

  const dismiss = useCallback(() => {
    setPanelOpen(false);
    setDialogOpen(false);
    setSelectedEventRule(undefined);
  }, []);

  const deleteEventRule = useCallback(() => {
    if (selectedEventRule === undefined) {
      return;
    }

    startLoading(
      () => api.deleteEventRule(selectedEventRule),
      () => {
        dismiss();
        refresh();
      },
    );
  }, [selectedEventRule]);

  const fetchEventRules = useCallback((pagination: Pagination) => api.getEventRules(pagination), [api]);

  return (
    <ContentControl
      panel={
        <EventRulePanel
          eventRule={selectedEventRule}
          closeHandler={() => {
            dismiss();
          }}
        />
      }
      dialog={
        <GenericDialog
          title={'Delete Event Rule'}
          text={selectedEventRule?.name ?? ''}
          type={'confirmation'}
          maxWidth={'sm'}
          onClose={dismiss}
          onConfirm={() => deleteEventRule()}
          fullWidth
          centered
          open={isDialogOpen}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          onClick={() => {
            dismiss();
            setSelectedEventRule(undefined);
            setPanelOpen(true);
          }}
          tooltip={'Add Event Rule'}
          requiredPermission={Permissions.EVENT_RULES_WRITE}
        />,
      ]}
    >
      <DataTable
        maxExpandedActions={3}
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: EventRule) => {
              dismiss();
              setSelectedEventRule(entry);
              setPanelOpen(true);
            },
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: EventRule) => {
              dismiss();
              setSelectedEventRule(entry);
              setDialogOpen(true);
            },
          },
          {
            title: 'Tasks',
            icon: <Engineering />,
            onClick: (entry: EventRule) => {
              const { name, coordinate } = entry;
              navigate(`/tasks/event-rules/${coordinate}/${name}`);
            },
          },
        ]}
        columns={columns}
        fetchItems={fetchEventRules}
      />
    </ContentControl>
  );
};
